// Thin fetch wrapper for the cloud API. Attaches the Cognito access token as a
// Bearer credential. The token is supplied by a provider the app wires from
// react-oidc-context (with a localStorage fallback so non-React callers work).

const BASE_URL = import.meta.env.VITE_APP_CLOUD_API_BASE_URL?.replace(
  /\/$/,
  "",
);

type TokenProvider = () => string | undefined;

let tokenProvider: TokenProvider = () => undefined;

/** Wire the access-token source (called once from the app with useAuth()). */
export const setAccessTokenProvider = (provider: TokenProvider) => {
  tokenProvider = provider;
};

/** Fallback: read the oidc-client-ts user from localStorage. */
const tokenFromStorage = (): string | undefined => {
  try {
    const authority = import.meta.env.VITE_APP_COGNITO_AUTHORITY;
    const clientId = import.meta.env.VITE_APP_COGNITO_CLIENT_ID;
    const raw = window.localStorage.getItem(
      `oidc.user:${authority}:${clientId}`,
    );
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw)?.id_token;
  } catch {
    return undefined;
  }
};

const getToken = (): string | undefined =>
  tokenProvider() ?? tokenFromStorage();

export class CloudApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const isCloudConfigured = Boolean(BASE_URL);

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BASE_URL) {
    throw new CloudApiError(0, "cloud API not configured");
  }
  const token = getToken();
  if (!token) {
    throw new CloudApiError(401, "not authenticated");
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: any = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      (payload && payload.error) || `request failed (${res.status})`;
    throw new CloudApiError(res.status, message);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
